import axios from 'axios'
import { getIntegrationURLFromENVForPLD } from '../../../utils/getIntegrationURLFromENV'
import { sendErrorLog } from './perfiosTransaction'
import { ErrorLogUploadType } from './constants/ErrorLogUploadType.enum'

function urlInit() {
  const API_URL_PLD = getIntegrationURLFromENVForPLD()
  return { API_URL_PLD }
}

// Flag to control CBS behavior - set to true to always use fallback, false to use real CBS API
// currently we always use inhouse cbs
const USE_INHOUSE_CBS = true


/**
 * Generate CBS Transaction ID (Perfios)
 * Endpoint: GET /perfios/cbs-reports/initiateTransaction
 * 
 * Response format: { transactionId: string, code: null, message: null, success: "true" }
 * No payload required (GET request)
 */
export async function initiateCbsTransaction() {
  // Helper function to store transaction ID in localStorage
  const storeTransactionId = (transactionId: string) => {
    const oldToken = window.localStorage.getItem('cbsTransactionId')
    if (oldToken) {
      window.localStorage.removeItem('cbsTransactionId')
    }
    window.localStorage.setItem('cbsTransactionId', transactionId)
  }

  // Helper function to generate fallback data
  const generateFallbackData = (message: string) => {
    const fallbackTransactionId = `FALLBACK${Date.now()}${Math.random().toString(36).substr(2, 9).toUpperCase()}`
    const fallbackData = {
      transactionId: fallbackTransactionId,
      code: null,
      message,
      success: "true"
    }
    storeTransactionId(fallbackTransactionId)
    return fallbackData
  }

  // If fallback mode is enabled, return fallback immediately
  if (USE_INHOUSE_CBS) {
    console.warn('CBS fallback mode enabled, using in-house CBS')
    return generateFallbackData('Using in-house CBS transaction ID')
  }

  // Try to call real CBS API
  try {
    const { API_URL_PLD } = urlInit()
    const { data } = await axios.get(API_URL_PLD + 'cbs-reports/initiate-transaction')
    
    // Validate response and store transaction ID
    if (data?.success === "true" && data?.transactionId) {
      storeTransactionId(data.transactionId)
      return data
    } else {
      console.warn('CBS API returned invalid response, using fallback')
      return generateFallbackData(data?.message || 'Invalid CBS response')
    }
  } catch (error: any) {
    await sendErrorLog('CBS_Initiate.json', '', '', error, ErrorLogUploadType.STATEMENT_UPLOAD)
    console.warn('CBS service error, using fallback:', error?.message)
    return generateFallbackData('CBS service error - using fallback')
  }
}

/**
 * Upload CBS report file (Perfios)
 * Endpoint: /perfios/cbs-reports/upload
 * 
 * Required fields: reportFile, applicationId, perfiosTransactionId, inputDate
 */
export async function uploadCbsReport(
  file: File,
  applicationId: string,
  inputDate: string,
  shareholderId: string
) {

  try {
    const { API_URL_PLD } = urlInit()
    const INTEGRATION_ENDPOINT = API_URL_PLD

    // Get stored CBS transaction ID
    const storedToken = window.localStorage.getItem('cbsTransactionId')
    if (!storedToken) {
      throw new Error('CBS transaction ID not found. Please initiate CBS transaction first.')
    }
    
    const perfiosTransactionId = storedToken
    if (!perfiosTransactionId) {
      throw new Error('Invalid CBS transaction ID found in storage.')
    }

    // Create FormData for multipart/form-data
    const formData = new FormData()
    formData.append('reportFile', file)
    formData.append('applicationId', applicationId)
    formData.append('perfiosTransactionId', perfiosTransactionId)
    formData.append('inputDate', inputDate)
    formData.append('shareholderId', shareholderId)

    const { data } = await axios.post(
      INTEGRATION_ENDPOINT + 'cbs-reports/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    
    return data
  } catch (error: any) {
    await sendErrorLog('CBS_Upload.json', '', '', error, ErrorLogUploadType.STATEMENT_UPLOAD)
    throw new Error(error?.response?.data?.message || error?.response?.data || error?.message)
  }
}

/**
 * Process CBS Report (Perfios)
 * Endpoint: POST /perfios/cbs-reports/process
 * 
 * CRITICAL: This should ONLY be called when user clicks "Save and Continue" button
 * Do NOT call this immediately after upload
 */
export async function processCbsReport() {
  let payload: any = undefined
  let storedToken: string | null = null
  try {
    const { API_URL_PLD } = urlInit()
    const INTEGRATION_ENDPOINT = API_URL_PLD

    // Get stored CBS transaction ID
    storedToken = window.localStorage.getItem('cbsTransactionId')
    if (!storedToken) {
      throw new Error('CBS transaction ID not found. Please initiate CBS transaction first.')
    }

    // Support both JSON stored token and raw string token
    let perfiosTransactionId: string | undefined
    try {
      const parsed = JSON.parse(storedToken)
      perfiosTransactionId = parsed?.perfiosTransactionId
    } catch (_e) {
      perfiosTransactionId = storedToken
    }

    if (!perfiosTransactionId) {
      throw new Error('Invalid CBS transaction ID found in storage.')
    }

    // Build payload for process API
    payload = {
      perfiosTransactionId,
    }

    const { data } = await axios.post(
      INTEGRATION_ENDPOINT + 'cbs-reports/process',
      payload
    )
    
    return data
  } catch (error: any) {
    await sendErrorLog('CBS_Process.json', '', '', error, ErrorLogUploadType.STATEMENT_UPLOAD)
    throw new Error(error?.response?.data?.message || error?.response?.data || error?.message)
  }
}
